import update from "immutability-helper";
import type { AnyAction } from "redux";

import {
  PORTALS_LOADING,
  PORTALS_LOADED,
  PORTAL_AUTOSAVE_STATUS_SET,
  PORTAL_CONTENT_LOADED,
  PORTAL_CONTENT_REPROCESSES_LOADED,
  PORTAL_CONTENT_TITLE_UPDATED,
  PORTAL_CONTENT_VISIBILITY_UPDATED,
  PORTAL_DELETED,
  PORTAL_DETAILS_LOADING,
  PORTAL_DETAILS_LOADED,
  PORTAL_PUBLISH_PROGRESS_LOADED,
  PORTAL_THUMBNAIL_UPDATED,
  PORTAL_UPDATED,
} from "../actions/types";

export type PortalStatus = "live" | "draft" | "unpublished";
export type PortalSyncMode = "auto-sync" | "manual";
export type PortalContentType = "story" | "clip";
export type PortalThumbnailFit = "cover" | "contain";
export type PortalDefaultLayout = "grid" | "list";
export type PortalContentReprocessStatus = "queued" | "processing" | "ready" | "failed";
export type PortalPublishProgressStatus =
  | "idle"
  | "uploading_artifacts"
  | "infra_started"
  | "infra_ready"
  | "infra_failed"
  | "publish_requested"
  | "nlp_queued"
  | "story_processing"
  | "story_ready"
  | "story_failed"
  | "ready"
  | "partial_failed"
  | "failed"
  | "unpublished";

export interface PortalColorTokens {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

export interface PortalContentItem {
  _id: string;
  type: PortalContentType;
  title: string;
  creator: string;
  duration: number;
  projectName: string;
  mediaType?: string;
  sourceStoryId?: string;
  thumbnailURL: string;
  hidden: boolean;
  supplementalFileIds?: string[];
}

export interface PortalCollection {
  _id: string;
  title: string;
  description: string;
  bannerURL: string;
  content: PortalContentItem[];
  hidden: boolean;
}

export interface PortalAppearance {
  carouselIntervalMs: number;
  coverImageURLs: string[];
  defaultLayout: PortalDefaultLayout;
  faviconImageURL: string;
  faviconVersion?: string;
  heroDescription?: string;
  heroTitle?: string;
  logoImageURL: string;
  primary: PortalColorTokens;
  secondary: PortalColorTokens;
}

export interface PortalMetadataFieldSelection {
  mode: "all" | "selected";
  fields: Array<{ key: string; projectId: string }>;
}

export interface PortalSettings {
  allowDownloads: boolean;
  contactEmail: string;
  defaultHomepage: string;
  discoverEnabled: boolean;
  metadataFieldSelection?: PortalMetadataFieldSelection;
  password?: string;
  visibility: string;
  zoteroEnabled: boolean;
}

export interface PortalMetadataFieldOption {
  projectId: string;
  projectName: string;
  fields: Array<{ key: string; label: string; source: string }>;
}

export type PortalDraftChangeStatus = "added" | "changed" | "unchanged";
export type PortalDraftChangeKind = "artifact" | "metadata" | "nlp" | "order";

export interface PortalDraftContentDiff {
  changedFields: string[];
  changeKinds: PortalDraftChangeKind[];
  status: PortalDraftChangeStatus;
}

export interface PortalDraftCollectionDiff {
  changeKinds: PortalDraftChangeKind[];
  changedFields: string[];
  contentById: Record<string, PortalDraftContentDiff>;
  hasContentChanges: boolean;
  removedContent: PortalContentItem[];
  status: PortalDraftChangeStatus;
}

export interface PortalDraftDiff {
  collectionsById: Record<string, PortalDraftCollectionDiff>;
  changeKinds: PortalDraftChangeKind[];
  hasChanges: boolean;
  hasContentChanges: boolean;
  portalChangedFields: string[];
  removedCollections: PortalCollection[];
}

export interface ResearchPortalDetail {
  _id: string;
  title: string;
  description: string;
  slug: string;
  shareUrl: string;
  thumbnailURL?: string;
  thumbnailFit?: PortalThumbnailFit;
  appearance: PortalAppearance;
  settings: PortalSettings;
  status: PortalStatus;
  infrastructureStatus?: "ready" | "failed";
  syncMode: PortalSyncMode;
  linkedProjectIds: string[];
  metadataFieldOptions: PortalMetadataFieldOption[];
  draftDiff: PortalDraftDiff;
  liveVersion?: {
    collections: PortalCollection[];
    appearance: PortalAppearance;
    description: string;
    linkedProjectIds: string[];
    prefix?: string;
    publishedAt: string;
    releaseId?: string;
    shareUrl: string;
    settings: PortalSettings;
    slug: string;
    thumbnailFit: PortalThumbnailFit;
    thumbnailURL: string;
    title: string;
  } | null;
  pendingLiveVersion?: {
    collections: PortalCollection[];
    appearance: PortalAppearance;
    description: string;
    linkedProjectIds: string[];
    prefix?: string;
    publishedAt: string;
    releaseId?: string;
    shareUrl: string;
    settings: PortalSettings;
    slug: string;
    thumbnailFit: PortalThumbnailFit;
    thumbnailURL: string;
    title: string;
  } | null;
  publishProgress?: {
    buildId?: string;
    errorMessage?: string;
    executionArn?: string;
    failedStories?: number;
    frontendUrl?: string;
    message?: string;
    notificationEmail?: string;
    notificationFailedAt?: string;
    notificationErrorMessage?: string;
    notificationSentAt?: string;
    readyStories?: number;
    expectedStories?: number;
    releaseId?: string;
    startedAt?: string;
    status: PortalPublishProgressStatus;
    currentCollectionTitle?: string;
    currentStoryId?: string;
    currentStoryStatus?: PortalPublishProgressStatus;
    currentStoryTitle?: string;
    updatedAt?: string;
  };
  contentReprocesses: Array<{
    collectionId: string;
    contentId: string;
    contentTitle: string;
    errorMessage?: string;
    jobId: string;
    releaseId: string;
    status: PortalContentReprocessStatus;
    updatedAt: string;
  }>;
  collections: PortalCollection[];
}

export interface PortalsState {
  isAutosavingPortal: boolean;
  isLoadingPortalDetail: boolean;
  isLoadingPortals: boolean;
  portals: ResearchPortalDetail[];
  portalDetail: ResearchPortalDetail;
}

const emptyPortal: ResearchPortalDetail = {
  _id: "",
  title: "",
  description: "",
  slug: "",
  shareUrl: "",
  thumbnailURL: "",
  thumbnailFit: "cover",
  appearance: {
    carouselIntervalMs: 5000,
    coverImageURLs: [],
    defaultLayout: "grid",
    faviconImageURL: "",
    faviconVersion: "",
    heroDescription: "",
    heroTitle: "",
    logoImageURL: "",
    primary: {
      main: "#1D9E75",
      light: "#d2ece3",
      dark: "#0c4231",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#185FA5",
      light: "#cfdfee",
      dark: "#0a2845",
      contrastText: "#ffffff",
    },
  },
  settings: {
    allowDownloads: false,
    contactEmail: "support@theirstory.io",
    defaultHomepage: "Featured content",
    discoverEnabled: true,
    password: "",
    visibility: "Public",
    zoteroEnabled: true,
  },
  status: "draft",
  infrastructureStatus: undefined,
  syncMode: "manual",
  linkedProjectIds: [],
  metadataFieldOptions: [],
  draftDiff: {
    collectionsById: {},
    changeKinds: [],
    hasChanges: false,
    hasContentChanges: false,
    portalChangedFields: [],
    removedCollections: [],
  },
  liveVersion: null,
  pendingLiveVersion: null,
  publishProgress: { status: "idle" },
  contentReprocesses: [],
  collections: [],
};

const initialState: PortalsState = {
  isAutosavingPortal: false,
  isLoadingPortalDetail: false,
  isLoadingPortals: false,
  portals: [],
  portalDetail: emptyPortal,
};

export const getPortalContent = (portal: ResearchPortalDetail) =>
  portal.collections.flatMap((collection) => collection.content);

const updatePortalCollectionContent = (
  portal: ResearchPortalDetail,
  collectionId: string,
  content: PortalContentItem[]
) => {
  const collections = portal.collections.map((collection) =>
    collection._id === collectionId ? { ...collection, content } : collection
  );

  return {
    ...portal,
    collections,
  };
};

const updatePortalContentItem = (
  portal: ResearchPortalDetail,
  collectionId: string,
  itemId: string,
  itemUpdate: Partial<PortalContentItem>
) => ({
  ...portal,
  collections: portal.collections.map((collection) => ({
    ...collection,
    content:
      collection._id === collectionId
        ? collection.content.map((item) => (item._id === itemId ? { ...item, ...itemUpdate } : item))
        : collection.content,
  })),
});

const portalsReducer = (state = initialState, { type, payload }: AnyAction) => {
  switch (type) {
    case PORTALS_LOADING:
      return update(state, {
        isLoadingPortals: {
          $set: true,
        },
      });

    case PORTALS_LOADED:
      return update(state, {
        isLoadingPortals: {
          $set: false,
        },
        portals: {
          $set: payload,
        },
      });

    case PORTAL_DETAILS_LOADING:
      return update(state, {
        isLoadingPortalDetail: {
          $set: true,
        },
      });

    case PORTAL_AUTOSAVE_STATUS_SET:
      return update(state, {
        isAutosavingPortal: {
          $set: payload,
        },
      });

    case PORTAL_DETAILS_LOADED: {
      const nextPortal = {
        ...emptyPortal,
        ...payload,
      };
      const exists = state.portals.some((portal) => portal._id === nextPortal._id);

      return update(state, {
        isLoadingPortalDetail: { $set: false },
        portalDetail: { $set: nextPortal },
        portals: exists
          ? { $set: state.portals.map((portal) => (portal._id === nextPortal._id ? nextPortal : portal)) }
          : { $push: [nextPortal] },
      });
    }

    case PORTAL_UPDATED: {
      const nextPortal = {
        ...emptyPortal,
        ...payload,
      };

      return update(state, {
        portalDetail: { $set: state.portalDetail._id === nextPortal._id ? nextPortal : state.portalDetail },
        portals: {
          $set: state.portals.map((portal) => (portal._id === nextPortal._id ? nextPortal : portal)),
        },
      });
    }

    case PORTAL_PUBLISH_PROGRESS_LOADED: {
      const publishUpdate = {
        draftDiff: payload.draftDiff,
        infrastructureStatus: payload.infrastructureStatus,
        liveVersion: payload.liveVersion,
        pendingLiveVersion: payload.pendingLiveVersion,
        publishProgress: payload.publishProgress,
        status: payload.status,
      };

      return update(state, {
        portalDetail: {
          $set:
            state.portalDetail._id === payload._id ? { ...state.portalDetail, ...publishUpdate } : state.portalDetail,
        },
        portals: {
          $set: state.portals.map((portal) => (portal._id === payload._id ? { ...portal, ...publishUpdate } : portal)),
        },
      });
    }

    case PORTAL_CONTENT_REPROCESSES_LOADED: {
      const reprocessUpdate = {
        contentReprocesses: payload.contentReprocesses,
        draftDiff: payload.draftDiff,
      };

      return update(state, {
        portalDetail: {
          $set:
            state.portalDetail._id === payload._id ? { ...state.portalDetail, ...reprocessUpdate } : state.portalDetail,
        },
        portals: {
          $set: state.portals.map((portal) =>
            portal._id === payload._id ? { ...portal, ...reprocessUpdate } : portal
          ),
        },
      });
    }

    case PORTAL_CONTENT_LOADED: {
      const nextContent = payload.content;
      const collectionId = payload.collectionId;
      const portalId = payload.portalId;

      if (!portalId) return state;

      const nextPortalDetail =
        state.portalDetail._id === portalId
          ? updatePortalCollectionContent(state.portalDetail, collectionId, nextContent)
          : state.portalDetail;

      return update(state, {
        portalDetail: {
          $set: nextPortalDetail,
        },
        portals: {
          $set: state.portals.map((portal) =>
            portal._id === portalId ? updatePortalCollectionContent(portal, collectionId, nextContent) : portal
          ),
        },
      });
    }

    case PORTAL_CONTENT_TITLE_UPDATED:
      return update(state, {
        portalDetail: {
          $set: updatePortalContentItem(state.portalDetail, payload.collectionId, payload.itemId, {
            title: payload.title,
          }),
        },
        portals: {
          $set: state.portals.map((portal) =>
            portal._id === state.portalDetail._id
              ? updatePortalContentItem(portal, payload.collectionId, payload.itemId, { title: payload.title })
              : portal
          ),
        },
      });

    case PORTAL_CONTENT_VISIBILITY_UPDATED:
      return update(state, {
        portalDetail: {
          $set: updatePortalContentItem(state.portalDetail, payload.collectionId, payload.itemId, {
            hidden: payload.hidden,
          }),
        },
        portals: {
          $set: state.portals.map((portal) =>
            portal._id === state.portalDetail._id
              ? updatePortalContentItem(portal, payload.collectionId, payload.itemId, { hidden: payload.hidden })
              : portal
          ),
        },
      });

    case PORTAL_THUMBNAIL_UPDATED:
      return update(state, {
        portalDetail: {
          draftDiff: { $set: payload.draftDiff },
          thumbnailURL: { $set: payload.thumbnailURL },
          thumbnailFit: { $set: payload.thumbnailFit },
        },
        portals: {
          $set: state.portals.map((portal) =>
            portal._id === payload.portalId
              ? {
                  ...portal,
                  draftDiff: payload.draftDiff,
                  thumbnailFit: payload.thumbnailFit,
                  thumbnailURL: payload.thumbnailURL,
                }
              : portal
          ),
        },
      });

    case PORTAL_DELETED:
      return update(state, {
        portalDetail: {
          $set: state.portalDetail._id === payload ? emptyPortal : state.portalDetail,
        },
        portals: {
          $set: state.portals.filter((portal) => portal._id !== payload),
        },
      });

    default:
      return state;
  }
};

export default portalsReducer;
